/**
 * Brazil's payout destinations (W9-DRIVER-PAYOUT-AND-METHODS, D470, ADR-0088; DevMobility F-031, I-028, I-040).
 *
 * The neutral `@vello/earnings` `PayoutDestinationSchema` for Brazil — country-pack DATA and rules, never a shared
 * contract. Two kinds:
 *
 *  - `bank_account`: the holder (name + CPF or CNPJ, check digits verified), the bank's COMPE code (3 digits) and,
 *    optionally, its ISPB (8 digits), the branch (agência, up to 4 digits + optional check digit), the account (up to 13
 *    digits + check digit, digit or X) and its type (corrente, poupança, pagamento);
 *  - `instant_payment_key` (a Pix key): the holder, the key type (CPF, CNPJ, e-mail, celular, chave aleatória) and the key
 *    — a CPF/CNPJ key passes its check digits, a phone key is normalized to E.164 under +55, an e-mail key is lower-cased,
 *    a random key is a UUID.
 *
 * The masked label never shows the holder's document nor more than the last digits of an account or key. The full,
 * normalized details are sealed in the credential vault by the api; the pack only validates.
 */
import { maskTail, payoutDestinationShapeOf } from '@vello/earnings';
import { isValidCnpj, isValidCpf } from './contact.js';
export const BRAZIL_PAYOUT_DESTINATION_SCHEMA_VERSION = 'BR.payoutDestination#v1';
const HOLDER = [
    { key: 'holderName', input: 'TEXT', required: true, maxLength: 120, labels: { pt: 'Nome do titular', en: 'Account holder name' } },
    { key: 'holderDocument', input: 'DIGITS', required: true, maxLength: 14, labels: { pt: 'CPF ou CNPJ do titular', en: 'Holder CPF or CNPJ' }, hint: { pt: 'Somente números', en: 'Digits only' } },
];
const KEY_TYPES = [
    { value: 'CPF', labels: { pt: 'CPF', en: 'CPF' } },
    { value: 'CNPJ', labels: { pt: 'CNPJ', en: 'CNPJ' } },
    { value: 'EMAIL', labels: { pt: 'E-mail', en: 'E-mail' } },
    { value: 'PHONE', labels: { pt: 'Celular', en: 'Mobile phone' } },
    { value: 'EVP', labels: { pt: 'Chave aleatória', en: 'Random key' } },
];
const ACCOUNT_TYPES = [
    { value: 'CHECKING', labels: { pt: 'Conta corrente', en: 'Checking account' } },
    { value: 'SAVINGS', labels: { pt: 'Conta poupança', en: 'Savings account' } },
    { value: 'PAYMENT', labels: { pt: 'Conta de pagamento', en: 'Payment account' } },
];
const EMAIL = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CHECK = /^[0-9X]$/;
const kinds = [
    {
        kind: 'bank_account',
        labels: { pt: 'Conta bancária', en: 'Bank account' },
        fields: [
            ...HOLDER,
            { key: 'bankCode', input: 'DIGITS', required: true, maxLength: 3, labels: { pt: 'Banco (código COMPE)', en: 'Bank (COMPE code)' } },
            { key: 'bankIspb', input: 'DIGITS', required: false, maxLength: 8, labels: { pt: 'ISPB do banco (opcional)', en: 'Bank ISPB (optional)' } },
            { key: 'branch', input: 'DIGITS', required: true, maxLength: 4, labels: { pt: 'Agência', en: 'Branch' } },
            { key: 'branchDigit', input: 'TEXT', required: false, maxLength: 1, labels: { pt: 'Dígito da agência (opcional)', en: 'Branch check digit (optional)' } },
            { key: 'account', input: 'DIGITS', required: true, maxLength: 13, labels: { pt: 'Conta', en: 'Account' } },
            { key: 'accountDigit', input: 'TEXT', required: true, maxLength: 1, labels: { pt: 'Dígito da conta', en: 'Account check digit' } },
            { key: 'accountType', input: 'CHOICE', required: true, maxLength: 8, labels: { pt: 'Tipo de conta', en: 'Account type' }, options: ACCOUNT_TYPES },
        ],
    },
    {
        kind: 'instant_payment_key',
        labels: { pt: 'Chave Pix', en: 'Pix key' },
        fields: [
            ...HOLDER,
            { key: 'keyType', input: 'CHOICE', required: true, maxLength: 5, labels: { pt: 'Tipo de chave', en: 'Key type' }, options: KEY_TYPES },
            { key: 'key', input: 'TEXT', required: true, maxLength: 77, labels: { pt: 'Chave', en: 'Key' } },
        ],
    },
];
const fail = (issue, field) => ({ ok: false, issue, ...(field !== undefined ? { field } : {}) });
const onlyDigits = (v) => v.replace(/\D/g, '');
function holderOf(f) {
    const name = (f.holderName ?? '').replace(/\s+/g, ' ').trim();
    if (name.length < 3)
        return { ok: false, v: fail('HOLDER_NAME_INVALID', 'holderName') };
    const document = onlyDigits(f.holderDocument ?? '');
    if (!(document.length === 11 ? isValidCpf(document) : document.length === 14 ? isValidCnpj(document) : false))
        return { ok: false, v: fail('HOLDER_DOCUMENT_INVALID', 'holderDocument') };
    return { ok: true, name, document };
}
/** Normalize a Pix key of a type, or undefined when it is not one. */
export function normalizeBrazilPixKey(keyType, raw) {
    const v = raw.trim();
    switch (keyType) {
        case 'CPF': {
            const d = onlyDigits(v);
            return d.length === 11 && isValidCpf(d) ? d : undefined;
        }
        case 'CNPJ': {
            const d = onlyDigits(v);
            return d.length === 14 && isValidCnpj(d) ? d : undefined;
        }
        case 'EMAIL': {
            const e = v.toLowerCase();
            return e.length <= 77 && EMAIL.test(e) ? e : undefined;
        }
        case 'PHONE': {
            let d = onlyDigits(v);
            if (d.length === 13 && d.startsWith('55'))
                d = d.slice(2);
            // DDD (2 digits, never 0 first) + a mobile number of 9 digits starting with 9.
            return /^[1-9][0-9]9\d{8}$/.test(d) ? `+55${d}` : undefined;
        }
        case 'EVP': {
            const k = v.toLowerCase();
            return UUID.test(k) ? k : undefined;
        }
        default:
            return undefined;
    }
}
function maskPixKey(keyType, key) {
    if (keyType === 'EMAIL') {
        const [local = '', domain = ''] = key.split('@');
        return `${local.slice(0, 1)}•••@${domain}`;
    }
    return maskTail(key, keyType === 'EVP' ? 4 : 3);
}
function validate(kind, raw) {
    const shape = payoutDestinationShapeOf({ kinds }, kind, raw);
    if (!shape.ok)
        return fail(shape.issue, shape.field);
    const f = shape.fields;
    const holder = holderOf(f);
    if (!holder.ok)
        return holder.v;
    if (kind === 'bank_account') {
        const bankCode = f.bankCode ?? '';
        if (!/^\d{3}$/.test(bankCode))
            return fail('BANK_CODE_INVALID', 'bankCode');
        const bankIspb = f.bankIspb;
        if (bankIspb !== undefined && !/^\d{8}$/.test(bankIspb))
            return fail('BANK_ISPB_INVALID', 'bankIspb');
        const branch = f.branch ?? '';
        if (!/^\d{1,4}$/.test(branch) || /^0+$/.test(branch))
            return fail('BRANCH_INVALID', 'branch');
        const branchDigit = f.branchDigit?.toUpperCase();
        if (branchDigit !== undefined && !CHECK.test(branchDigit))
            return fail('BRANCH_DIGIT_INVALID', 'branchDigit');
        const account = (f.account ?? '').replace(/^0+(?=\d)/, '');
        if (!/^\d{1,13}$/.test(account) || /^0+$/.test(account))
            return fail('ACCOUNT_INVALID', 'account');
        const accountDigit = (f.accountDigit ?? '').toUpperCase();
        if (!CHECK.test(accountDigit))
            return fail('ACCOUNT_DIGIT_INVALID', 'accountDigit');
        const details = {
            holderName: holder.name, holderDocument: holder.document, bankCode, branch, account, accountDigit, accountType: f.accountType ?? '',
            ...(bankIspb !== undefined ? { bankIspb } : {}),
            ...(branchDigit !== undefined ? { branchDigit } : {}),
        };
        return { ok: true, details, maskedLabel: `${bankCode} · ${branch} · ${maskTail(`${account}${accountDigit}`, 4)}` };
    }
    const keyType = f.keyType ?? '';
    const key = normalizeBrazilPixKey(keyType, f.key ?? '');
    if (key === undefined)
        return fail('PIX_KEY_INVALID', 'key');
    return { ok: true, details: { holderName: holder.name, holderDocument: holder.document, keyType, key }, maskedLabel: `Pix · ${maskPixKey(keyType, key)}` };
}
/** Brazil's payout destination schema (the api registers it under `BR`). */
export const BRAZIL_PAYOUT_DESTINATION_SCHEMA = {
    country: 'BR',
    schemaVersion: BRAZIL_PAYOUT_DESTINATION_SCHEMA_VERSION,
    kinds,
    validate,
};
//# sourceMappingURL=payout-destination.js.map